/**
 * El HERO de la home para los rubros que no tienen catálogo propio —
 * ADR-2026-09-20-la-home-de-los-otros-rubros-abre-con-el-agente.
 *
 * Reemplaza a las dos cards de "Elegí qué querés crear" (desktop) y al carrusel de
 * modos (mobile) por lo primero con lo que conviene que esos usuarios se encuentren:
 * **la caja del Agente**, escribible ahí mismo. Para una marca de gastronomía, de
 * servicios o de tecnología la tira de modos es una lista de herramientas armadas para
 * vender ropa; la pregunta que sí le sirve es "¿qué necesitás?".
 *
 * Los modos no se esconden: viven en la otra mitad del switch —**Herramientas**—, con
 * una solapa por familia (Imágenes / Videos) y la MISMA grilla de cards que el popup de
 * vertiente (`GrillaDeModos`). Un switch y no dos secciones apiladas porque son dos
 * formas de arrancar lo mismo, no dos pasos: si estuvieran las dos a la vista, la caja
 * dejaría de ser "la primera instancia" y volvería a ser una card más.
 *
 * ENCABEZA con el saludo de la home, centrado, y debajo el switch
 * (ADR-2026-09-21-el-saludo-se-centra-arriba-del-switch). Un rato tuvo además la mascota
 * y el título tipeado del chat a la izquierda del switch; se fueron porque eran un
 * segundo título preguntando lo mismo, y la mascota pasó a ser el ícono de la solapa
 * "Agente" (ADR-2026-09-21-la-mascota-es-el-icono-del-agente).
 *
 * Los DOS switches —el de arriba y el de familias, adentro de Herramientas— son el mismo
 * `SwitchDePastillas`, con la pastilla que se desliza de una opción a la otra
 * (ADR-2026-09-21-los-switches-de-la-home-se-deslizan).
 *
 * ES EL MISMO COMPONENTE EN DESKTOP Y EN MOBILE (`variante` cambia tamaños, no piezas):
 * el pedido era que el teléfono se comportara igual, y dos copias del mismo hero se
 * desincronizan al primer retoque.
 *
 * Es PRESENTACIONAL salvo por una cosa: abrir el Agente. Los modos, su candado y su
 * `onAbrir` entran por props desde quien ya los tiene resueltos (`InterfazV2`).
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Image as ImageIcon, LayoutGrid, Video as VideoIcon } from 'lucide-react';
import { CumbreMascot } from '../../../components/CumbreMascot';
// El switch con la pastilla que se desliza: vive en `components/` porque también lo usa
// la fila de pastillas de la caja del Agente (ADR-2026-09-21-el-switch-de-imagen-o-video).
import { SwitchDePastillas } from '../../../components/SwitchDePastillas';
// La caja del Agente ya cableada al contexto del chat
// (ADR-2026-09-21-la-caja-de-la-home-es-la-del-agente). El contexto GRANDE se lee ahí
// adentro y no acá: así lo que se re-renderiza con cada token del stream es la caja y no
// el switch, la grilla de modos y la home entera.
import { CajaDelAgenteHome } from '../../../chat-atlas/ui/CajaDelAgenteHome';
import { SUGERENCIAS_AGENTE } from '../../../chat-atlas/ui/estilosAgente';
import { RUTA_CHAT_ATLAS } from '../../../chat-atlas/ui/rutaChatAtlas';
import type { ModoHome } from '../../estilos/EstiloMinimal';
import { GrillaDeModos } from './GrillaDeModos';

type Solapa = 'agente' | 'herramientas';
type Familia = 'imagen' | 'video';

/**
 * Los modos que NO se listan en la solapa "Herramientas"
 * (ADR-2026-09-21-modelo-sale-de-la-solapa-herramientas).
 *
 * Es un filtro de ESTA superficie y no un `ocultoEnListas` del catálogo: "Modelo" sigue
 * en `/modos`, en la sidebar, en el sheet de "Generar" y en el popup de vertiente. Acá
 * la lista es corta a propósito —seis filas que se leen de un vistazo debajo de la caja
 * del Agente— y el modo entra por las otras puertas.
 */
const FUERA_DE_HERRAMIENTAS = new Set<string>(['modelo']);

/** Las dos solapas de Herramientas. El copy es el mismo que usan las cards de vertiente
 *  y el paso 1 del sheet de Generar: una sola forma de nombrar las dos familias. */
const FAMILIAS: { id: Familia; label: string; Icono: typeof ImageIcon }[] = [
  { id: 'imagen', label: 'Imágenes', Icono: ImageIcon },
  { id: 'video', label: 'Videos', Icono: VideoIcon },
];



/**
 * El verbo MÁS LARGO de la lista. Se dibuja invisible debajo del que se tipea para
 * reservarle el ancho: acá el título comparte renglón con el switch, y sin esto cada
 * letra que entra o sale lo correría de lugar (en el arranque del Agente el h1 está solo
 * y centrado, así que el temblor no se nota; acá haría bailar un control clickeable).
 */

export interface HeroAgenteHomeProps {
  /** Los modos de la solapa Herramientas, con su familia, su candado y su `onAbrir`. */
  modos: ModoHome[];
  /** Rubro de la marca activa: decide la carátula de los modos que tienen una propia. */
  rubroMarca: string | null;
  /**
   * El plan no incluye el Agente (`getAgenteLock`, Pro y Ultra —
   * ADR-2026-09-14-el-agente-es-de-pro-y-ultra). La caja se sigue viendo —es lo que
   * explica qué se está perdiendo— pero enviar lleva a los planes, y el hero abre en
   * Herramientas: la mitad que sí puede usar.
   */
  agenteBloqueado: boolean;
  /** Abrir un modo, con el gating del llamador (bloqueado → ejemplos o planes). */
  onAbrirModo: (m: ModoHome) => void;
  /** A dónde manda el envío cuando el Agente está bloqueado. */
  onIrAPlanes: () => void;
  /** `mobile` achica la mascota, la caja y la grilla. No cambia ninguna pieza. */
  variante?: 'desktop' | 'mobile';
  /**
   * El saludo de la home ("Buenas noches, Maxi / ¿Qué quisieras crear hoy?"), CENTRADO
   * arriba del switch — ADR-2026-09-21-el-saludo-se-centra-arriba-del-switch.
   *
   * Entra como nodo y no se arma acá: es el mismo `<h1>` de la home, con el mismo texto
   * y la misma hora del día. Lo que cambia es dónde vive, no qué dice. Sin él, el hero
   * arranca con el switch, que es como se monta en el teléfono.
   */
  saludo?: ReactNode;
}

export function HeroAgenteHome({
  modos,
  rubroMarca,
  agenteBloqueado,
  onAbrirModo,
  onIrAPlanes,
  variante = 'desktop',
  saludo,
}: HeroAgenteHomeProps) {
  const esMobile = variante === 'mobile';
  const navigate = useNavigate();
  // Sin acceso el hero abre en Herramientas: la caja queda a un toque, pero lo que se
  // ve primero es lo que la cuenta sí puede usar.
  const [solapa, setSolapa] = useState<Solapa>(agenteBloqueado ? 'herramientas' : 'agente');
  const [familia, setFamilia] = useState<Familia>('imagen');
  /** El pill de sugerencia que hay que volcar en la caja. El contador es lo que la caja
   *  mira: la misma sugerencia dos veces tiene que volver a completar y enfocar. */
  const [sugerencia, setSugerencia] = useState<{ texto: string; token: number }>();

  const porFamilia = useMemo(() => {
    // Sin `familia` (no debería pasar: la pone `InterfazV2`) el modo cae en imagen, el
    // mismo defecto que usan la home y `useModosGenerar`.
    const deLaSolapa = modos.filter((m) => !FUERA_DE_HERRAMIENTAS.has(m.path));
    const imagen = deLaSolapa.filter((m) => m.familia !== 'video');
    const video = deLaSolapa.filter((m) => m.familia === 'video');
    return { imagen, video };
  }, [modos]);

  /**
   * La familia que se DIBUJA. `familia` es la que el usuario eligió; si esa quedó
   * vacía —un rubro que sólo tiene modos de video, o una lista que todavía no llegó—
   * se cae a la otra. Sin esto, la solapa elegida no existe como pestaña (se ocultan
   * las vacías) y el panel queda en blanco sin que se pueda salir.
   */
  const familiaActiva: Familia =
    porFamilia[familia].length > 0 ? familia : porFamilia.imagen.length > 0 ? 'imagen' : 'video';

  return (
    // `relative`: el hero no tiene nada absoluto propio, pero sí tiene que ser un
    // contexto de posición para lo que venga. La ESTELA ya no vive acá — se fue a la
    // raíz de la home, para que la luz arranque en el techo y no debajo del encabezado
    // (ADR-2026-09-21-la-estela-arranca-en-el-techo-de-la-home). Sin `isolate`, que era
    // para encerrarle el z negativo: ahora el hero entero se pinta encima de ella.
    <section className={`relative ${esMobile ? 'mt-2' : 'mt-6'}`}>
      {/* El saludo, CENTRADO y arriba del switch — sale de la fila del encabezado, donde
          compartía renglón con los accesos de cuenta. Acá la pantalla se lee de arriba
          abajo por el eje del medio: quién sos y qué querés hacer, con qué, y la caja.
          En el teléfono TAMBIÉN va, desde
          ADR-2026-09-21-el-saludo-tambien-va-en-el-telefono: lo pasa `InterfazV2`, que
          trae el nombre por su cuenta porque abajo no existe el contexto de cuenta del
          shell. Ahí se acerca al switch (`mb-5`): la pantalla es más corta y con el
          mismo aire la caja se iba abajo del pliegue. */}
      {/* `relative z-10`: el saludo y el switch van SIEMPRE por encima de la estela.
          Hoy salen encima solos —la estela es `-z-10` en la raíz de la home—, pero es la
          clase de cosa que se rompe sin que nadie la toque el día que algo del medio
          estrene un contexto de apilado, y es justo lo que no se puede dejar al azar:
          son el título y el control de la pantalla, no decoración. */}
      {saludo && (
        <div className={`relative z-10 text-center ${esMobile ? 'mb-5' : 'mb-7'}`}>{saludo}</div>
      )}

      {/* El switch, centrado y solo en su renglón.
          Acá vivieron un rato la mascota y el título tipeado del arranque del Agente
          ("Creá / Preguntá / … sin límites") a la izquierda del switch, en la misma fila
          (ADR-2026-09-21-la-mascota-y-el-verbo-encabezan-la-home). Se fueron: con el
          saludo de la home ya centrado arriba, eran DOS títulos, uno abajo del otro,
          preguntando lo mismo — y el de abajo, además, cambiando de palabra solo.

          La mascota no se perdió: es el ícono de la solapa "Agente"
          (ADR-2026-09-21-la-mascota-es-el-icono-del-agente), que es donde dice algo —de
          qué lado del switch está el agente— en vez de decorar.

          El fondo gris con las pastillas adentro es el mismo par de valores que usan los
          toggles de la app (chip activo en blanco con sombra finita). */}
      <div className="relative z-10 flex justify-center">
        <SwitchDePastillas
          tema="claro"
          valor={solapa}
          onCambiar={setSolapa}
          opciones={[
            {
              id: 'agente' as Solapa,
              label: 'Agente',
              // Decorativa: la etiqueta de al lado ya dice qué es. `cumbre--mini` baja
              // el float y la sombra, calibrados para los 120px del arranque del chat: a
              // 20px, enteros, hacen saltar la pastilla y dejan una mancha azul.
              // Apagada cuando la solapa no es la activa: el ícono de Herramientas
              // hereda el gris de su botón y la mascota no —es un SVG con gradiente
              // propio—, así que sin esto la mitad inactiva del switch se ve encendida.
              icono: (
                <CumbreMascot
                  size={20}
                  decorativa
                  className={`cumbre--mini h-auto shrink-0 transition-opacity ${
                    solapa === 'agente' ? '' : 'opacity-45'
                  }`}
                />
              ),
            },
            {
              id: 'herramientas' as Solapa,
              label: 'Herramientas',
              icono: <LayoutGrid className="h-4 w-4 shrink-0" strokeWidth={2.25} />,
            },
          ]}
        />
      </div>

      {solapa === 'agente' ? (
        // EL 80% DEL ANCHO DISPONIBLE — ADR-2026-09-22-la-caja-y-las-herramientas-miden-lo-mismo.
        // La caja del Agente y el panel de Herramientas son las dos mitades del MISMO
        // switch: midiendo distinto, el contenido saltaba de ancho al cambiar de solapa.
        // Las dos miden `w-4/5` de la columna de la home, centradas, y el 20% que queda es
        // el aire que las despega del borde. En el teléfono van a todo el ancho: el 80% de
        // 390px son márgenes, no aire.
        <div
          className={`mx-auto flex flex-col items-center ${
            esMobile ? 'mt-5 w-full' : 'mt-7 w-4/5'
          }`}
        >
          {/* La caja es LA MISMA barra de entrada del Agente —"+ Agregar" con su menú
              y sus selectores del catálogo, el selector de Inteligencia, el micrófono y
              el botón de enviar— y no una caja parecida
              (ADR-2026-09-21-la-caja-de-la-home-es-la-del-agente). */}
          <div className="w-full">
            <CajaDelAgenteHome
              variante={esMobile ? 'panel' : 'pantalla'}
              bloqueado={agenteBloqueado}
              onBloqueado={onIrAPlanes}
              // Mandado el turno, a la pantalla del Agente: lo que sigue a un pedido
              // escrito de cero es una conversación, no una franja al costado de una
              // home que ya no se está mirando.
              onEnviado={() => navigate(RUTA_CHAT_ATLAS)}
              sugerencia={sugerencia}
            />
          </div>

          {agenteBloqueado && (
            // Se dice antes de tocar y no después: la caja se ve y se puede escribir,
            // pero el envío va a los planes, y enterarse recién ahí sería una puerta
            // que se abre para cerrarse.
            <p className="mt-2.5 text-center text-[12.5px] leading-snug text-black/45">
              El Agente viene con los planes Pro y Ultra
            </p>
          )}

          {/* Las sugerencias COMPLETAN la caja y dejan el cursor ahí; no mandan
              (ADR-2026-09-12-las-sugerencias-completan-la-caja). Son las MISMAS dos del
              arranque del Agente: si en la home dijeran otra cosa, serían dos catálogos
              de lo que el agente sabe hacer.

              Desde ADR-2026-09-22-las-sugerencias-del-agente-son-dos son dos y no
              cuatro, así que entran enteras en los dos tamaños; la fila del teléfono
              sigue pudiendo correrse por si mañana vuelven a ser más. */}
          <div
            className={`mt-4 flex gap-2 ${
              esMobile
                ? '-mx-5 w-[calc(100%+2.5rem)] overflow-x-auto px-5 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
                : 'flex-wrap justify-center'
            }`}
          >
            {SUGERENCIAS_AGENTE.map(({ key, label, prompt, Icon }) => (
              <button
                key={key}
                type="button"
                // `onMouseDown` con `preventDefault`: que el click no le saque el foco a
                // la caja, que es donde el cursor tiene que quedar.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setSugerencia({ texto: prompt, token: Date.now() })}
                // En el teléfono van un punto más chicos y con menos padding: es el
                // tamaño con el que las dos entran cómodas en el ancho del teléfono.
                className={`inline-flex shrink-0 items-center rounded-full bg-[#E9EDFD] font-semibold text-brand-primary transition-colors hover:bg-[#D8E0FB] ${
                  esMobile ? 'gap-1.5 px-3 py-1.5 text-[12px]' : 'gap-1.5 px-3.5 py-1.5 text-[12.5px]'
                }`}
              >
                <Icon className={`shrink-0 ${esMobile ? 'h-3.5 w-3.5' : 'h-[15px] w-[15px]'}`} strokeWidth={2.25} />
                <span className="whitespace-nowrap">{label}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        /* Herramientas: un panel con las dos familias en solapas y la grilla de modos
           adentro. El panel existe para que la grilla no se lea como "la home ahora es
           una tira de modos": es el contenido de la otra mitad del switch. */
        <div
          className={`mx-auto mt-5 rounded-2xl bg-white ring-1 ring-black/[0.06] ${
            // El MISMO ancho que la caja del Agente (ver el comentario de la otra rama).
            esMobile ? 'w-full p-4' : 'w-4/5 p-5'
          }`}
        >
          {/* TABS, no un segmented control: sin la pista gris atrás, cada familia es una
              pastilla suelta y sólo la elegida se pinta
              (ADR-2026-09-21-imagenes-y-videos-pasan-a-ser-tabs). El switch de arriba
              —Agente / Herramientas— SÍ sigue siendo un segmented: ahí las dos mitades
              son dos formas de arrancar la misma pantalla y la pista las junta; acá son
              dos filtros de la grilla de abajo, y dos controles con la misma pista, uno
              debajo del otro, se leían como el mismo control repetido.

              La elegida va en AZUL CLARITO con el texto y el ícono en `brand-primary`
              —el mismo par que los pills de sugerencia del Agente—, no en gris: sobre el
              panel blanco un chip gris es casi el mismo valor que el fondo y no se ve
              cuál está activa. El ícono toma el color solo (`currentColor`). */}
          <div className="flex justify-center">
            <SwitchDePastillas
              tema="azul"
              valor={familiaActiva}
              onCambiar={setFamilia}
              // Una familia sin modos no tiene solapa: en vez de una pestaña que abre un
              // panel vacío, no está. Con una sola opción el switch sigue andando — la
              // pastilla se queda quieta donde está, que es lo correcto.
              opciones={FAMILIAS.filter(({ id }) => porFamilia[id].length > 0).map(
                ({ id, label, Icono }) => ({
                  id,
                  label,
                  icono: <Icono className="h-[15px] w-[15px] shrink-0" strokeWidth={2.25} />,
                }),
              )}
            />
          </div>

          {/* En LISTA y no en cards (ADR-2026-09-21-las-herramientas-van-en-lista): dos
              columnas de filas con la miniatura a la izquierda y el nombre + la bajada a
              su derecha. Con cards, la solapa era una segunda tira de modos en el medio
              de la home y le peleaba la atención a la caja del Agente, que es lo que
              tiene que mandar; en fila entran las mismas herramientas en un tercio del
              alto y se leen de un vistazo. En el teléfono, una sola columna. */}
          <GrillaDeModos
            modos={porFamilia[familiaActiva]}
            rubroMarca={rubroMarca}
            onAbrir={onAbrirModo}
            anatomia="lista"
            className={`mt-4 grid gap-x-6 gap-y-1 ${esMobile ? 'grid-cols-1' : 'sm:grid-cols-2'}`}
          />
        </div>
      )}
    </section>
  );
}

export default HeroAgenteHome;
